/**
 * Submit handler for the additional-information form.
 *
 * Posts to the new dcapi endpoint (config.submitUrl). On accept, redirects.
 * Response shape is documented in types.ts:AdditionalInfoSubmitResponse —
 * confirm the actual field names with the dcapi team and adjust the parser
 * below if needed.
 */

import type {
  AdditionalInfoFormConfig,
  AdditionalInfoSubmitResponse,
} from './types';

export interface SubmitOutcome {
  ok: boolean;
  accepted: boolean;
  /** True ONLY when dcapi explicitly returned status:"decline". */
  declined: boolean;
  redirectUrl?: string;
  scenario?: number;
  raw?: AdditionalInfoSubmitResponse;
  error?: string;
}

export async function submitAdditionalInfo(
  config: AdditionalInfoFormConfig,
  additionalInformation: Record<string, string>
): Promise<SubmitOutcome> {
  if (!config.submitUrl) {
    return { ok: false, accepted: false, declined: false, error: 'submitUrl not configured' };
  }

  // Merge in fields carried forward from the lead form (loan_amount, home_owner)
  // so they're submitted without re-asking the user. Carry-forward values win
  // over any same-named form field (there are none today) and may be non-string
  // (loan_amount is an integer). Resolved lazily — see getCarryForwardFields.
  const carryForward = config.getCarryForwardFields?.() ?? {};
  const additionalInfoPayload: Record<string, unknown> = {
    ...additionalInformation,
    ...carryForward,
  };

  const payload = {
    session_id: config.sessionId,
    additional_information: additionalInfoPayload,
    tracking_data: config.trackingData ?? {},
  };

  try {
    const response = await fetch(config.submitUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'omit',
      body: JSON.stringify(payload),
    });

    // dcapi returns its error shape (status/hasError/errorMessage) in the body
    // on BOTH HTTP 200 (lead-not-found, unhandled) and HTTP 400 (missing field),
    // so parse the body regardless of status code and let the parser decide.
    let raw: AdditionalInfoSubmitResponse | null = null;
    try {
      const parsed = await response.json();
      // n8n's "Respond to Webhook" can return the object wrapped in an array
      // ([{...}]) — the same quirk the credit-pull endpoint has (see
      // credit-decision-poller's array unwrap). Without this, the top-level
      // status/accept/redirect_url read as undefined → accept is missed and the
      // browser never redirects. Unwrap to the first element when array-wrapped.
      raw = (Array.isArray(parsed) ? parsed[0] : parsed) as AdditionalInfoSubmitResponse;
    } catch {
      raw = null;
    }

    if (config.debug) {
      console.log('[additional-info] raw dcapi response:', raw, '(http', response.status + ')');
    }

    if (!raw) {
      return {
        ok: false,
        accepted: false,
        declined: false,
        error: `submit failed: ${response.status} ${response.statusText}`,
      };
    }

    return parseSubmissionResponse(raw);
  } catch (err) {
    return {
      ok: false,
      accepted: false,
      declined: false,
      error: err instanceof Error ? err.message : String(err),
    };
  }
}

/**
 * Parse the dcapi response into a normalized outcome.
 *
 * Accepts either `status: "accept"` or `accept: true` to signal acceptance.
 * Accepts either `redirect_url` or `redirectUrl` for the redirect target.
 */
function parseSubmissionResponse(
  raw: AdditionalInfoSubmitResponse
): SubmitOutcome {
  // dcapi signals failure in the body via hasError/status:"error" on both
  // HTTP 200 (lead-not-found, unhandled) and HTTP 400 (missing field). Treat
  // that as a hard failure so the controller surfaces it visibly instead of
  // silently routing it as a non-accept.
  const statusStr = typeof raw.status === 'string' ? raw.status.toLowerCase() : '';
  if (raw.hasError === true || statusStr === 'error' || statusStr === 'failed') {
    const error =
      raw.errorMessage || raw.error_message || raw.message || 'dcapi returned an error';
    return { ok: false, accepted: false, declined: false, error: String(error), raw };
  }

  // Accept — either form (PartPost2 or Dot818) accepted; redirect URL present.
  const accepted = raw.accept === true || statusStr === 'accept';
  // Decline — dcapi explicitly returned status:"decline" (scenario 7). This is
  // the ONLY signal that should swap the form for the decline page.
  const declined = !accepted && statusStr === 'decline';
  const redirectUrl = raw.redirect_url || raw.redirectUrl || undefined;

  // ok:true covers both accept (redirect) and decline (status:"decline",
  // scenario 7) — the controller routes decline via the non-accept event.
  return {
    ok: true,
    accepted,
    declined,
    redirectUrl,
    scenario: typeof raw.scenario === 'number' ? raw.scenario : undefined,
    raw,
  };
}

/**
 * Perform the redirect. Centralized so the controller can short-circuit if
 * the browser is being torn down or the URL is missing/invalid.
 */
export function performAcceptRedirect(url: string, debug = false): void {
  if (!url) {
    if (debug) console.warn('[additional-info] accept response had no redirect URL');
    return;
  }
  if (debug) console.log('[additional-info] redirecting to', url);
  window.location.href = url;
}
